export enum Role {
  ADMIN = 1,
  INSTRUCTOR = 2,
  TEACHING_ASSISTANT = 3,
  HR = 4,
  KOL = 5,
  OWNER = 6,
  /** Community site owner-level access; treated as a normal user on Edunodes (eztech). */
  WEB_EDITOR = 7,
}

export const roleLabels: Record<Role, string> = {
  [Role.ADMIN]: 'Admin',
  [Role.INSTRUCTOR]: 'Instructor',
  [Role.TEACHING_ASSISTANT]: 'Teaching assistant',
  [Role.HR]: 'Hiring agent',
  [Role.KOL]: 'KOL',
  [Role.OWNER]: 'Owner',
  [Role.WEB_EDITOR]: 'Web editor',
}

/** Short capability text shown next to each role in create/edit user UI. */
export const roleDescriptions: Record<Role, string> = {
  [Role.ADMIN]: 'Manage users, courses, and most community admin settings.',
  [Role.INSTRUCTOR]: 'Create and edit assigned courses; teach enrolled learners.',
  [Role.TEACHING_ASSISTANT]: 'Support instructors on assigned courses; limited edit access.',
  [Role.HR]: 'Manage hiring-related content (FSDS only).',
  [Role.KOL]: 'KOL / influencer access (FSDS only).',
  [Role.OWNER]: 'Full community ownership, including transferring ownership.',
  [Role.WEB_EDITOR]: 'Edit community website pages and branding.',
}

/** Roles that can be selected in admin UI. */
export const ASSIGNABLE_ROLES = Object.values(Role).filter((role): role is Role => typeof role === 'number')

/** FSDS-only roles hidden from Edunodes / community create-user and list filters. */
export const FSDS_ONLY_ROLES: Role[] = [Role.HR, Role.KOL]

/** Roles with owner-level access on the community site (Owner and Web editor). */
export const OWNER_LEVEL_ROLES: Role[] = [Role.OWNER, Role.WEB_EDITOR]

export function hasOwnerLevelRole(roles?: Role[] | null): boolean {
  if (!roles?.length) return false
  return roles.some(role => OWNER_LEVEL_ROLES.includes(role))
}

/** Admin, Owner, or Web editor — can open the admin dashboard. */
export function hasAdminDashboardAccess(roles?: Role[] | null): boolean {
  if (!roles?.length) return false
  return roles.includes(Role.ADMIN) || hasOwnerLevelRole(roles)
}

/** Only community owners may assign Owner / Web editor to others. */
export function canAssignOwnerLevelRoles(roles?: Role[] | null): boolean {
  return Boolean(roles?.includes(Role.OWNER))
}

export type AssignableRolesOptions = {
  /** When false (Edunodes communities), hide FSDS-only roles like Hiring agent / KOL. */
  isFsdsDomain?: boolean
}

/** Roles the actor may toggle in admin user create/edit. */
export function getAssignableRolesForActor(
  roles?: Role[] | null,
  options?: AssignableRolesOptions
): Role[] {
  let assignable = canAssignOwnerLevelRoles(roles)
    ? ASSIGNABLE_ROLES
    : ASSIGNABLE_ROLES.filter(role => !OWNER_LEVEL_ROLES.includes(role))

  if (options?.isFsdsDomain === false) {
    assignable = assignable.filter(role => !FSDS_ONLY_ROLES.includes(role))
  }

  return assignable
}
